import type { InputHTMLAttributes, ReactNode } from "react";
import { useId } from "react";
import { cn } from "../cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  /** Shown under the field. Explains what went wrong and how to fix it. */
  error?: string;
  hint?: ReactNode;
}

/**
 * A labelled text field.
 *
 * The label is always associated with the control and the error is wired to it
 * through aria-describedby, so a screen reader reaches the reason a field was
 * rejected rather than only the fact that it was.
 */
export function Input({ label, error, hint, className, id, ...props }: InputProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={inputId} className="font-sans text-ui text-ink-2">
          {label}
        </label>
      ) : null}
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(error && errorId, hint && hintId) || undefined}
        className={cn(
          "h-9 rounded-[var(--radius-control)] bg-ground px-3 font-sans text-ui text-ink",
          "border border-rule placeholder:text-ink-3",
          "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent",
          error && "border-critical",
          className,
        )}
        {...props}
      />
      {hint && !error ? (
        <p id={hintId} className="text-meta text-ink-3">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-meta text-critical-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}
